'use client';

import classNames from 'classnames';
import {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useModalDialog } from '../hooks/useModalDialog';
import Button from './Button';
import Close from './icons/Close';

const AsideModalCloseContext = createContext<() => void>(() => {});

const ASIDE_MODAL_SELECTOR = '[data-component="aside-modal"]';

// A modal with another one open inside it (the PL Offers panel inside the PDP
// drawer) leaves Escape and outside clicks to that one, so only the topmost
// closes - instead of both at once, each writing its own history entry.
const hasNestedAsideModal = (root: HTMLElement | null) =>
  !!root?.querySelector(ASIDE_MODAL_SELECTOR);

/**
 * Closes the surrounding aside modal the way its own controls do — playing the
 * slide-out transition before the content unmounts. Content that closes the
 * panel by clearing the state behind it instead makes it vanish in one frame.
 *
 * A no-op outside a panel, so content shared with plain pages can call it
 * unconditionally.
 */
export const useAsideModalClose = () => useContext(AsideModalCloseContext);

type AsideModalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  size?: 'normal' | 'small' | 'pdp';
  /**
   * Hide the built-in floating close button — for content that renders its own
   * close control in a header frame (via `useAsideModalClose`). Default keeps
   * the floating button, so existing modals are untouched.
   */
  hideCloseButton?: boolean;
  children: ReactNode;
  buttonCloseClassNames?: string;
  /**
   * `buttonCloseClassNames` cannot reach the glyph: that needs an arbitrary
   * variant, which this Tailwind config does not generate.
   */
  closeIconClassNames?: string;
  /** English by default: a shared component with no translation context. */
  closeButtonLabel?: string;
  backgroundClassNames?: string;
  onCloseButtonClick?: () => void;
  /**
   * Opt-in: renders the panel as a modal dialog with this name — the page
   * behind it inert and focus kept inside. Escape already closes it.
   */
  dialog?: {
    label: string;
    id?: string;
    returnFocusRef?: RefObject<HTMLElement | null>;
  };
};

/**
 * Slide-in aside modal (right panel).
 *
 * Behaviors:
 * - Locks body scroll while open (adds `overflow-hidden` + compensates scrollbar width).
 * - Closes on outside click (delayed slightly to allow other UI interactions to run first).
 * - Closes on `Escape`.
 * - Uses `isVisible` for enter/exit animation timing; `onClose` fires after transition.
 *
 * Props:
 * - `isOpen`: whether the modal is mounted/open.
 * - `onClose`: called after the close animation completes.
 * - `onCloseButtonClick`: called by the header close button only, so a panel can
 *   tell that dismissal apart from an outside click or `Escape`.
 * - `size`: controls responsive width (`normal` | `small`).
 * - `children`: modal content.
 */
export const AsideModal: FC<AsideModalModalProps> = (props) => {
  const { onClose, children, isOpen, size } = props;
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  // If the modal is open and a click occurs outside its boundaries,
  // this ref stores a timeout ID to delay the closing action,
  // allowing other interactive elements (like comparison table cards)
  // to potentially trigger a state change (e.g., opening a new modal)
  // before this modal attempts to close itself.
  const clickOutsideTimeoutRef = useRef<number | null>(null);

  const closeModal = useCallback(() => {
    setIsVisible(false);
    const timeout = setTimeout(() => {
      onClose();
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [onClose, setIsVisible]);

  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        if (isOpen && !hasNestedAsideModal(modalRef.current)) {
          clickOutsideTimeoutRef.current = setTimeout(() => {
            closeModal();
          }, 150) as unknown as number;
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutsideMenu);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
  }, [closeModal, isOpen]);

  useEffect(() => {
    clearTimeout(clickOutsideTimeoutRef.current!);
  }, [children]);

  useBodyScrollLock(isOpen);

  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const setModalElement = useCallback((element: HTMLDivElement | null) => {
    modalRef.current = element;
    setRoot(element);
  }, []);
  useModalDialog(props.dialog ? root : null, {
    active: isOpen,
    returnFocusRef: props.dialog?.returnFocusRef,
  });

  useEffect(() => {
    const visibilityTimeout = setTimeout(() => {
      setIsVisible(isOpen);
    }, 200);

    return () => {
      clearTimeout(visibilityTimeout);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (!isOpen || hasNestedAsideModal(modalRef.current)) {
        return;
      }
      if (event.key === 'Escape' && !event.defaultPrevented) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [closeModal, isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={setModalElement}
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={classNames(
        'pointer-events-none fixed inset-0 z-[var(--z-index-modal)] bg-black/50 transition-opacity duration-500 ease-in-out',
        {
          hidden: !isOpen,
          'opacity-100 pointer-events-auto': isVisible,
          'opacity-0 pointer-events-none': !isVisible,
        },
      )}
      // Not `button`, here or on the panel below: children of a `button` are
      // presentational, so both wrappers pruned the modal's whole subtree —
      // dialog, combobox and listbox — out of the accessibility tree.
      onClick={() => {
        if (!hasNestedAsideModal(modalRef.current)) closeModal();
      }}
      role="presentation"
      data-component="aside-modal"
    >
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className={classNames(
          'absolute inset-0 z-50 md:inset-y-0 md:left-auto',
          'border-l border-borders/default bg-fill/alternative-1',
          props.backgroundClassNames,
          'no-scrollbar overflow-y-auto',
          'pointer-events-auto',
          'transition-transform duration-500 ease-in-out',
          {
            'translate-x-full': !isVisible,
            'shadow-[0_0_16px_0_#00000040]': isVisible,
            'w-full md:inset-y-0 md:left-auto md:w-7/12 lg:w-3/4 2xl:w-[1536px]':
              size === undefined || size === 'normal',
            'w-full md:inset-y-0 md:w-[640px] lg:w-[720px]': size === 'small',
            // #usePDP ProductDetails modal only (CMS-1924, designer spec):
            // <768 full-screen sheet, 768-1279 448px, >=1280 1080px
            // (project breakpoints: md=768, lg=1280).
            'w-full md:inset-y-0 md:w-[448px] lg:w-[1080px]': size === 'pdp',
          },
        )}
        onClick={(e) => e.stopPropagation()}
        role={props.dialog ? 'dialog' : 'presentation'}
        aria-modal={props.dialog ? true : undefined}
        aria-label={props.dialog?.label}
        id={props.dialog?.id}
      >
        {!props.hideCloseButton && (
          <div className="fixed right-4 top-1 z-10 md:right-6 md:top-3">
            <Button
              // Icon-only, so it has no text to name it.
              aria-label={props.closeButtonLabel ?? 'Close'}
              endIcon={
                <Close className={props.closeIconClassNames ?? 'size-6'} />
              }
              styleType="neutral"
              className={classNames(
                'flex size-11 items-center justify-center border-0 !p-0 shadow-md',
                props.buttonCloseClassNames,
              )}
              variant="outline"
              onClick={() => {
                props.onCloseButtonClick?.();
                closeModal();
              }}
            />
          </div>
        )}
        <AsideModalCloseContext.Provider value={closeModal}>
          <div>{children}</div>
        </AsideModalCloseContext.Provider>
      </div>
    </div>
  );
};
